import React, { useState, useEffect } from "react";
import {
	Modal,
	Radio,
	Checkbox,
	Row,
	Col,
	Button,
	Divider,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";

const Wrapper = styled.div`
  position: relative;

  .category-header {
	display: flex;
	justify-content: space-between;
	margin-top: 20px;
	font-weight: 500;
  }

  .selector-wrapper {
	position: relative;
	margin-top: 10px;
  }

  .selector-box {
	border: 1px solid #d9d9d9;
	border-radius: 8px;
	padding: 12px 14px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	cursor: pointer;
	background: #fff;
  }

  .popover-box {
	position: absolute;
	bottom: 110%;
	top: auto;
	left: 0;
	width: 100%;
	background: #ffffff;
	border-radius: 10px;
	padding: 16px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
	z-index: 1000;
  }

  .reset-text {
	color: #1677ff;
	cursor: pointer;
	font-size: 13px;
  }

  .footer-btn {
	text-align: right;
	margin-top: 25px;
  }
`;

const SendAnnouncementModal = ({
	open,
	onClose,
	roleList = [],
	locationList = [],
	onConfirmSelection,
	initialSendType = "All",
	initialRoleIds = [],
	initialLocationIds = [],
}) => {
	const [sendType, setSendType] = useState("All");
	const [openRole, setOpenRole] = useState(false);
	const [openLocation, setOpenLocation] = useState(false);
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [selectedCountries, setSelectedCountries] = useState([]);

	const userList = useSelector(getUserList);
	const totalEmployees = userList?.filter((user) => user.statusid === "1")?.length || 0;

	const locationOptions = [
    ...new Map(
        locationList
            .filter((l) => l.country)
            .map((l) => [l.country, { id: String(l.location_id), name: l.country }])
    ).values(),
];

	useEffect(() => {
		if (open) {
			const hasSpecific = initialRoleIds?.length > 0 || initialLocationIds?.length > 0;
			setSendType(hasSpecific ? "Specific" : initialSendType);
			setSelectedRoles(initialRoleIds?.map(String) || []);
			// initialLocationIds may be country strings or location ids — keep as-is
			setSelectedCountries(initialLocationIds?.map(String) || []);
			setOpenRole(false);
			setOpenLocation(false);
		}
	}, [open, initialSendType, initialRoleIds, initialLocationIds]);

	const handleConfirm = () => {
		if (sendType === "All") {
			onConfirmSelection({
				sendType: "all",
				roleIds: [],
				locationIds: [],
				items: ["All Employees"],
			});
			onClose();
			return;
		}

		const roleNames = roleList
			.filter((r) => selectedRoles.includes(String(r.roleId)))
			.map((r) => r.roleName);

		const locationNames = locationOptions
			.filter((l) => selectedCountries.includes(l.id))
			.map((l) => l.name);

		onConfirmSelection({
			sendType: "specific",
			roleIds: selectedRoles,
			locationIds: selectedCountries,       
			items: [...locationNames, ...roleNames], 
		});

		onClose();
	};

	return (
		<Modal
			open={open}
			footer={null}
			onCancel={onClose}
			width={650}
			title="Send announcement to"
		>
			<Wrapper>
				<Radio.Group
					value={sendType}
					onChange={(e) => setSendType(e.target.value)}
				>
					<div style={{ marginBottom: 10 }}>
						<Radio value="All">All Employees</Radio>
					</div>
					<Radio value="Specific">Specific Employees</Radio>
				</Radio.Group>

				{sendType === "Specific" && (
					<>
						<div className="category-header">
							<span>Specify the category</span>
							<span>{totalEmployees} Employees</span>
						</div>

						{/* ================= ROLE ================= */}
						<div className="selector-wrapper">
							<div
								className="selector-box"
								onClick={() => {
									setOpenRole(!openRole);
									setOpenLocation(false);
								}}
							>
								<span>
									Role {selectedRoles?.length > 0 && `(${selectedRoles?.length})`}
								</span>
								<DownOutlined />
							</div>

							{openRole && (
								<div className="popover-box">
									<Row justify="space-between">
										<Checkbox
											checked={selectedRoles?.length === roleList?.length}
											onChange={(e) =>
												setSelectedRoles(
													e.target.checked
														? roleList.map((r) => String(r.roleId))
														: []
												)
											}
										>
											All
										</Checkbox>
										<span
											className="reset-text"
											onClick={() => setSelectedRoles([])}
										>
											Reset
										</span>
									</Row>

									<Divider style={{ margin: "10px 0" }} />

									<Checkbox.Group
										style={{ width: "100%" }}
										value={selectedRoles}
										onChange={setSelectedRoles}
									>
										<Row>
											{roleList.map((role) => (
												<Col span={12} key={role.roleId}>
													<Checkbox value={String(role.roleId)}>
														{role.roleName}
													</Checkbox>
												</Col>
											))}
										</Row>
									</Checkbox.Group>
								</div>
							)}
						</div>

						{/* ================= LOCATION ================= */}
						<div className="selector-wrapper">
							<div
								className="selector-box"
								onClick={() => {
									setOpenLocation(!openLocation);
									setOpenRole(false);
								}}
							>
								<span>
									Location{" "}
									{selectedCountries?.length > 0 && `(${selectedCountries?.length})`}
								</span>
								<DownOutlined />
							</div>

							{openLocation && (
								<div className="popover-box">
									<Row justify="space-between">
										<Checkbox
											checked={
												selectedCountries?.length === locationOptions?.length &&
												locationOptions?.length > 0
											}
											onChange={(e) =>
												setSelectedCountries(
													e.target.checked ? locationOptions.map((l) => l.id) : []
												)
											}
										>
											All
										</Checkbox>
										<span
											className="reset-text"
											onClick={() => setSelectedCountries([])}
										>
											Reset
										</span>
									</Row>

									<Divider style={{ margin: "10px 0" }} />

									<Checkbox.Group
										style={{ width: "100%" }}
										value={selectedCountries}
										onChange={setSelectedCountries}
									>
										<Row>
											{locationOptions.map((loc) => (
												<Col span={12} key={loc.id}>
													<Checkbox value={loc.id}>
														{loc.name}
													</Checkbox>
												</Col>
											))}
										</Row>
									</Checkbox.Group>
								</div>
							)}
						</div>
					</>
				)}

				<div className="footer-btn">
					<Button onClick={onClose} style={{ marginRight: 10 }}>
						Cancel
					</Button>
					<Button
						type="primary"
						style={{
							background: "#B7EBD7",
							border: "none",
							color: "#000",
						}}
						onClick={handleConfirm}
					>
						Confirm
					</Button>
				</div>
			</Wrapper>
		</Modal>
	);
};

export default SendAnnouncementModal;